import {
	Pagination,
	PaginationContent,
	PaginationItem,
	PaginationPrevious,
	PaginationLink,
	PaginationEllipsis,
	PaginationNext,
} from "./pagination";

interface PaginatorProps {
	pageCount: number;
	pageIndex: number;
	shownPages: number;
	showRelative?: boolean; // Si se muestra la navegación relativa
	onPageChange: (pageIndex: number) => void;

	canPrevious: boolean;
	canNext: boolean;
}
export function Paginator(props: PaginatorProps) {
	const {
		pageCount,
		pageIndex,
		shownPages,
		onPageChange,
		canPrevious,
		canNext,
	} = props;
	const { showRelative = true } = props;

	const navigateRelative = (delta: number) => (e: React.MouseEvent) => {
		e.preventDefault();
		const newPageIndex = pageIndex + delta;
		if (newPageIndex >= 0 && newPageIndex < pageCount) {
			onPageChange(newPageIndex);
		}
	};
	const navigateToPage = (page: number) => (e: React.MouseEvent) => {
		e.preventDefault();
		if (page >= 0 && page < pageCount) {
			onPageChange(page);
		}
	};

	return (
		<Pagination>
			<PaginationContent>
				{showRelative && (
					<PaginationItem>
						<PaginationPrevious
							onClick={navigateRelative(-1)}
							disabled={!canPrevious}
						/>
					</PaginationItem>
				)}

				<Pages
					pageCount={pageCount}
					pageIndex={pageIndex}
					shownPages={shownPages}
					navigateToPage={navigateToPage}
				/>

				{showRelative && (
					<PaginationItem>
						<PaginationNext
							onClick={navigateRelative(+1)}
							disabled={!canNext}
						/>
					</PaginationItem>
				)}
			</PaginationContent>
		</Pagination>
	);
}

function Pages(props: PagesProps) {
	const { pageCount, pageIndex, shownPages, navigateToPage } = props;

	if (pageCount == 0) {
		return (
			<PaginationItem>
				<PaginationLink disabled isActive>
					1
				</PaginationLink>
			</PaginationItem>
		);
	}

	// Show all pages if total pages is less than or equal to shownPages
	if (pageCount <= shownPages) {
		return (
			<>
				{Array.from({ length: pageCount }, (_, i) => (
					<PaginationItem key={i + 1}>
						<PaginationLink
							onClick={navigateToPage(i)}
							isActive={pageIndex === i}
						>
							{i + 1}
						</PaginationLink>
					</PaginationItem>
				))}
			</>
		);
	}

	const pages = [];
	const firstPage = 1;
	const lastPage = pageCount;

	const windowSize = shownPages - 2; // always show first and last
	let start = Math.max(pageIndex + 1 - Math.floor(windowSize / 2), 2);
	let end = start + windowSize - 1;

	if (end > lastPage - 1) {
		end = lastPage - 1;
		start = Math.max(end - windowSize + 1, 2);
	}

	const startEllipsis = start > 2; // Show ellipsis if there are pages before the window
	const endEllipsis = end < lastPage - 1; // Show ellipsis if there are pages after the window

	// Si hay elipsis al final, quitamos la pagina que este ocupa
	if (endEllipsis) {
		end -= 1;
	}
	// Si hay elipsis al principio, quitamos la pagina que este ocupa
	if (startEllipsis) {
		start += 1;
	}

	// First page
	pages.push(
		<PaginationItem key={firstPage}>
			<PaginationLink onClick={navigateToPage(0)} isActive={pageIndex === 0}>
				{firstPage}
			</PaginationLink>
		</PaginationItem>,
	);

	if (startEllipsis) {
		pages.push(
			<PaginationItem key="start-ellipsis">
				<PaginationEllipsis />
			</PaginationItem>,
		);
	}

	// Page window
	for (let i = start; i <= end; i++) {
		pages.push(
			<PaginationItem key={i}>
				<PaginationLink
					onClick={navigateToPage(i - 1)}
					isActive={pageIndex === i - 1}
				>
					{i}
				</PaginationLink>
			</PaginationItem>,
		);
	}

	// Ellipsis after window
	if (endEllipsis) {
		pages.push(
			<PaginationItem key="end-ellipsis">
				<PaginationEllipsis />
			</PaginationItem>,
		);
	}

	// Last page
	pages.push(
		<PaginationItem key={lastPage}>
			<PaginationLink
				onClick={navigateToPage(lastPage - 1)}
				isActive={pageIndex === lastPage - 1}
			>
				{lastPage}
			</PaginationLink>
		</PaginationItem>,
	);

	return <>{pages}</>;
}

interface PagesProps {
	navigateToPage: (page: number) => (e: React.MouseEvent) => void;
	pageIndex: number;
	pageCount: number;
	shownPages: number;
}

// export const Paginator = memo(_Paginator);
